import { LucideIcon, MoreHorizontal } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export interface ActionItem {
  label: string
  icon?: LucideIcon
  onClick?: () => void
  disabled?: boolean
  variant?: 'default' | 'destructive'
  className?: string
  /** Hidden unless the staff member holds this permission. */
  permission?: string
}

interface ActionDropdownProps {
  trigger?: React.ReactNode
  label?: string
  items: ActionItem[]
  className?: string
}

export function ActionDropdown({ trigger, label, items, className }: ActionDropdownProps) {
  const { can } = useAuth()
  const allowed = items.filter((item) => !item.permission || can(item.permission))
  if (allowed.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger || (
          <Button variant="ghost" className={cn('h-8 w-8 p-0', className)}>
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {label && <DropdownMenuLabel>{label}</DropdownMenuLabel>}
        {allowed.map((item, index) => (
          <DropdownMenuItem
            key={index}
            onClick={item.onClick}
            disabled={item.disabled}
            className={cn(
              item.variant === 'destructive' ? 'text-error-dark focus:text-error-dark' : '',
              item.className
            )}
          >
            {item.icon && <item.icon className="mr-2 h-4 w-4" />}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
